/**
 * Which machines run which version of a design system, in words (design systems round 2):
 * "4 machines on 1.2.0, 2 on 1.1.0, 3 not yet". Shared by the Design systems page and the
 * Customisations page's per-machine view. Round 3 adds the Cloud allowance, where a version came
 * from, and the fonts' licences.
 */

import { designSystemOriginWords, quotaAmountText, type CloudQuotaRow, type DesignSystemFont, type DesignSystemOrigin } from '@aioe/shared';

import type { DesignSystemSpread } from '../api/client.ts';

const machines = (n: number) => `${n} ${n === 1 ? 'machine' : 'machines'}`;

export function spreadWords(s: DesignSystemSpread): string {
  const parts = s.versions.map((v, i) => `${i === 0 ? machines(v.machines.length) : v.machines.length} on ${v.version}`);
  if (s.notYet.length) {
    const words = s.delivery === 'installed' || s.delivery === undefined ? 'not yet' : 'have not added it';
    parts.push(parts.length ? `${s.notYet.length} ${words}` : `${machines(s.notYet.length)} ${words}`);
  }
  return parts.length ? parts.join(', ') : 'No machine says it has it yet';
}

/**
 * Where the fleet stands on it: `behind` when a machine runs an earlier version (or an installed
 * one has not arrived), `current` when every machine that has it runs the current one.
 */
export function standing(s: DesignSystemSpread): 'current' | 'behind' | undefined {
  if (!s.current) return undefined;
  if (s.versions.some((v) => !v.current) || (s.delivery === 'installed' && s.notYet.length)) return 'behind';
  return s.versions.length ? 'current' : undefined;
}

/** "1 of 10 on Teams", "3.2 MB of 400 MB on Teams", "No limit": a design-system quota row in words (round 3). */
export function allowanceWords(r: CloudQuotaRow | undefined, plan: string): string | undefined {
  if (!r || r.used === undefined) return undefined;
  if (r.limit === null) return `${quotaAmountText(r.unit, r.used)}, no limit`;
  if (r.limit === 0) return `Not part of ${plan}`;
  return `${quotaAmountText(r.unit, r.used)} of ${quotaAmountText(r.unit, r.limit)} on ${plan}`;
}

/** Whether a quota row is at its limit. */
export const atLimit = (r: CloudQuotaRow | undefined) => Boolean(r && r.limit !== null && r.used !== undefined && r.used >= r.limit);

/** Where a version came from and who brought it, in words: "Imported from harbour.zip (Harbour Pty Ltd) by JP". */
export function provenanceWords(origin: DesignSystemOrigin | undefined, publishedBy?: string): string | undefined {
  const words = designSystemOriginWords(origin);
  const by = origin?.byName ?? publishedBy;
  if (!words) return publishedBy ? `Published by ${publishedBy}` : undefined;
  return by ? `${words} by ${by}` : words;
}

/** The fonts and their licence statements: "Inter (OFL-1.1), Acme Sans (Commercial, desktop and web)". */
export function licenceWords(fonts: DesignSystemFont[] | undefined): string | undefined {
  if (!fonts?.length) return undefined;
  return `${fonts.length === 1 ? 'Font' : 'Fonts'}: ${fonts.map((f) => `${f.name} (${f.licence})`).join(', ')}`;
}
